/**
 * Time Clock v2 — the punches made on THIS device (rebuilt Oct 29 2026): what is still
 * waiting to be sent (offline), and the server's answer for each one that was sent
 * (applied, kept for a manager's review, rejected with the reason).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, CloudOff, History, RefreshCw, XCircle } from 'lucide-react';
import { useTimeclock } from '../context/TimeclockContext';
import { ACTION_LABEL } from '@/lib/timeclock/api';
import { listPunches, QUEUE_CHANGED } from '@/lib/timeclock/punchQueue';
import { fmtDate, fmtTime } from '@/lib/timeclock/time';

const STATUS = {
  pending: { label: 'Waiting to send', icon: CloudOff, cls: 'text-sky-300 bg-sky-500/10 ring-sky-400/30' },
  applied: { label: 'On the clock', icon: CheckCircle2, cls: 'text-emerald-300 bg-emerald-500/10 ring-emerald-400/30' },
  review: { label: 'For manager review', icon: AlertTriangle, cls: 'text-amber-300 bg-amber-500/10 ring-amber-400/30' },
  rejected: { label: 'Not accepted', icon: XCircle, cls: 'text-rose-300 bg-rose-500/10 ring-rose-400/30' },
};

export default function HistoryPage({ onBack, footer = null }) {
  const { online, pending, syncing, syncNow, timezone, lastSync } = useTimeclock();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await listPunches());
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(QUEUE_CHANGED, load);
    return () => window.removeEventListener(QUEUE_CHANGED, load);
  }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex flex-col" data-testid="clock-history">
      <header className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
        <button type="button" onClick={onBack} className="rounded-xl p-2 text-slate-400 hover:bg-white/5 hover:text-white" aria-label="Back to the clock" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <History className="w-5 h-5 text-emerald-300" />
        <h1 className="text-white font-semibold">Punches on this device</h1>
        <button type="button" onClick={syncNow} disabled={!online || syncing || pending === 0}
          className="ml-auto inline-flex items-center gap-2 rounded-xl bg-slate-800 px-3 py-2 text-sm text-white disabled:opacity-40" data-testid="sync-now">
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> Send now
        </button>
      </header>
      <main className="flex-1 w-full max-w-2xl mx-auto px-5 py-6 space-y-4">
        <p className="text-sm text-slate-400">
          {online ? 'Online.' : 'Offline.'} {pending} punch{pending === 1 ? '' : 'es'} waiting to be sent.
          {lastSync && ` Last sent ${fmtTime(lastSync.at.toISOString(), timezone)}: ${lastSync.applied} on the clock, ${lastSync.review} for review, ${lastSync.rejected} not accepted.`}
        </p>
        {error && <p className="text-rose-300 text-sm">{error}</p>}
        {items.length === 0 ? (
          <p className="rounded-2xl bg-slate-900 p-6 text-center text-slate-400 ring-1 ring-white/5">
            No punch has been kept on this device. Punches made online go straight to the clock.
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => {
              const s = STATUS[p.status] || STATUS.pending;
              const Icon = s.icon;
              return (
                <li key={p.clientPunchId} className="rounded-2xl bg-slate-900 p-4 ring-1 ring-white/5" data-testid="device-punch">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{p.staffName || 'Employee'} · {ACTION_LABEL[p.action] || p.action}{p.action === 'BREAK_START' ? (p.breakPaid ? ' (paid)' : ' (unpaid)') : ''}</p>
                      <p className="text-xs text-slate-400">{fmtDate(p.occurredAt, timezone)} · {fmtTime(p.occurredAt, timezone)}</p>
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 ${s.cls}`}>
                      <Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                  </div>
                  {p.message && p.status !== 'applied' && <p className="mt-2 text-xs text-slate-400">{p.message}</p>}
                </li>
              );
            })}
          </ul>
        )}
        {footer}
      </main>
    </div>
  );
}
